import React, { useCallback, useMemo } from 'react';
import { RRule, Weekday } from 'rrule';
import { NavStack } from '@shared/util/navigation';
import * as RecurrencePresets from '../../presets';
import { matchesWeekday } from '../RRuleHelpers';
import { RecurrencePickerRouteNames } from '../RecurrencePickerRoutes';
import { DescriptiveRecurrenceHeader } from './DescriptiveRecurrenceHeader';
import { RepeatEveryLabel } from './RepeatEveryLabel';
import { SelectableDays } from './SelectableDays';

interface Props {
  rruleString: string | undefined;
  onChange(rule: RRule | undefined): void;
  nav: NavStack<RecurrencePickerRouteNames>;
}

export function WeeklyRecurrencePicker({ rruleString, onChange, nav }: Props) {
  const rule = useMemo(
    () => (!!rruleString ? RRule.fromString(rruleString) : undefined),
    [rruleString],
  );

  const openIntervalPicker = useCallback(() => {
    nav.push({ name: 'selectWeeklyInterval' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = useCallback(
    (day: Weekday) => {
      const prevInterval = rule?.options.interval;
      if (rule?.options.freq !== RRule.WEEKLY) {
        onChange(RecurrencePresets.weekly([day]));
      } else if (matchesWeekday(rule, day)) {
        const otherDays = rule.options.byweekday.filter(d => d !== day.weekday);
        if (!otherDays.length) {
          onChange(undefined);
        } else {
          onChange(RecurrencePresets.weekly(otherDays, prevInterval));
        }
      } else {
        const weekdays = [...rule.options.byweekday, day.weekday].sort();
        const updatedRule = RecurrencePresets.weekly(weekdays, prevInterval);
        onChange(updatedRule);
      }

      nav.replace({ name: 'weeklyRecurrence' });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rule, onChange],
  );

  return (
    <>
      {!!rule && <DescriptiveRecurrenceHeader rule={rule} />}
      <RepeatEveryLabel
        intervalValue={rule?.options.interval ?? 1}
        intervalPluralStringKey={'card.blocks.recurrenceInput.weekly.xWeeks'}
        isPressable={!rule}
        onPress={openIntervalPicker}
      />
      <SelectableDays rruleString={rruleString} onChange={handleToggle} />
    </>
  );
}
